import React, { useState, useContext, useRef, useEffect } from 'react';

import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';

import { REPLY_ITEMS_PER_PAGE } from '../../../apis/khoros/constants';
import {
  useKhorosEditReply,
  useKhorosReply,
  useKhorosUser,
} from '../../../apis/khoros/hooks';
import { KhorosReplyItemSchema } from '../../../apis/khoros/type/khoros-replies.type';
import { useUserProfile } from '../../../apis/useUserSearch';
import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import I18nContext from '../../../aurr/contexts/I18nContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import { ToastContext } from '../../../aurr/contexts/ToastContext';
import { ProfileAvatar } from '../../container/user';

interface ReplyFormProps {
  parentId: string;
  baseURL?: string;
  userContentURL?: string;
  userProfileURL?: string;
  topicId: string;
  comment?: KhorosReplyItemSchema;
  onEditTentative?: () => void;
  onEditSuccess?: () => void;
  onEditFail?: () => void;
}

export default function ReplyForm({
  parentId,
  baseURL,
  userContentURL,
  userProfileURL,
  topicId,
  onEditTentative,
  onEditSuccess,
  onEditFail,
  comment,
}: ReplyFormProps) {
  const { userProfile } = useContext(LoginContext);
  const { setErrorToast } = useContext(ToastContext);
  const { kh_body, kh_post_id } = comment || {};

  const parsedBody =
    kh_body?.replace(/(\\r)|(\\n)/g, '\n')?.replace(/(<([^>]+)>)/gi, '') || '';

  const { t } = useContext(I18nContext);
  const { USER_PROFILE_URL, USER_CONTENT_URL, KHOROS_API } =
    useContext(ConfigContext);

  const [isEditing, setIsEditing] = useState(false);
  const [isActive, setIsActive] = useState(
    Boolean(parsedBody && parsedBody.length)
  );
  const [newReply, setNewReply] = useState(parsedBody);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: user } = useUserProfile({
    oxygenId: userProfile?.sub || '',
    userProfileURL: userProfileURL || USER_PROFILE_URL,
    userContentURL: userContentURL || USER_CONTENT_URL,
  });

  const { firstName, lastName } = user || {};

  const topicProps = {
    baseURL: baseURL || KHOROS_API,
    topicId,
  };

  const { data: khorosUserData, isLoading: isKhorosUserLoading } =
    useKhorosUser({
      baseURL: baseURL || KHOROS_API,
    });

  const parsedName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : khorosUserData?.kh_user_login || '';

  const {
    mutateAsync: onReplyComment,
    isPending: replyCommentLoading,
    isError: replyCommentIsError,
    reset: replyCommentReset,
  } = useKhorosReply({
    ...topicProps,
    commentId: parentId || '',
    pageSize: REPLY_ITEMS_PER_PAGE,
  });

  const {
    mutateAsync: onReplyUpdate,
    isPending: updateReplyLoading,
    isError: updateReplyIsError,
    reset: updateReplyReset,
  } = useKhorosEditReply({
    ...topicProps,
    parentId,
    commentId: kh_post_id || '',
  });

  const isLoading = replyCommentLoading || updateReplyLoading;
  const isError = replyCommentIsError || updateReplyIsError;

  const handleFocus = () => {
    setIsActive(true);
  };

  const handleBlur = () => {
    setIsActive(Boolean(newReply && newReply.length));
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value || '';
    setNewReply(value);
  };

  const handleCloseError = () => {
    replyCommentReset();
    updateReplyReset();
  };

  const handleClick = async () => {
    handleCloseError();

    if (newReply?.trim() === '') {
      setIsActive(false);
      onEditSuccess?.();
      return;
    }

    if (newReply && kh_post_id) {
      onEditTentative?.();
      setIsEditing(true);
      setIsActive(false);
      try {
        await onReplyUpdate(newReply);
        onEditSuccess?.();
        return;
      } catch (e) {
        setIsEditing(false);
        setErrorToast({
          message: t('comments.postError', { ns: 'common' }),
        });
        setIsActive(true);
        onEditFail?.();
        return;
      }
    }

    if (newReply) {
      try {
        setNewReply('');
        setIsActive(false);
        await onReplyComment(newReply);
      } catch (e) {
        setErrorToast({
          message: t('comments.postError', { ns: 'common' }),
        });
        setNewReply(newReply);
        setIsActive(true);
      }
    }
  };

  useEffect(() => {
    const textareaEl =
      textareaRef.current?.querySelector<HTMLTextAreaElement>('textarea[name]');
    if (isActive && textareaEl && document.activeElement !== textareaEl) {
      const cursorPosition = textareaEl.value.length + 7;
      textareaEl.focus();
      textareaEl.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [isActive]);

  const uniqueId = `replyForm-${kh_post_id || 'new'}-${Math.random()}`;

  return isEditing ? null : (
    <div className="au-flex au-gap-2 au-pl-12">
      <ProfileAvatar
        alt={t('avatarFor', { ns: 'common', fullName: parsedName })}
        size="medium"
        src={khorosUserData?.kh_user_avatar}
        fullName={parsedName}
        isLoading={isKhorosUserLoading}
      />
      <div className="au-grow">
        <FormControl
          fullWidth
          sx={{
            marginBottom: '12px',
          }}
        >
          <InputLabel
            htmlFor={uniqueId}
            sx={{
              color: 'white',
              '&.Mui-focused': {
                color: 'white !important',
              },
            }}
          >
            {t('comments.reply', { ns: 'common' })}
          </InputLabel>
          <Input
            ref={textareaRef}
            id={uniqueId}
            multiline
            rows={isActive ? 4 : 1}
            disabled={isLoading}
            error={isError}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            name="commentForm"
            value={newReply}
            placeholder={t('comments.replyPlaceholder', { ns: 'common' })}
            sx={{
              '& .MuiInputBase-input': {
                color: 'white !important',
                border: '1px solid white',
                '&::placeholder': {
                  color: 'white !important',
                  opacity: 1,
                },
              },
            }}
          />
        </FormControl>
        {isActive ? (
          <div className="au-flex au-justify-between">
            <div className="au-flex au-flex-col au-text-c12 au-text-gray-300">
              <span>{t('comments.beNicePolicy', { ns: 'common' })}</span>
              <span>{t('comments.bePositive', { ns: 'common' })}</span>
            </div>
            <button
              type="button"
              className="au-uppercase au-bg-[#FEFF09] au-text-black au-py-1 au-px-3 au-rounded-sm au-text-c12"
              disabled={!newReply || newReply.length === 0 || isLoading}
              onClick={() => handleClick()}
            >
              {t('comments.post', { ns: 'common' })}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
